import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckSquare, Search } from 'lucide-react';
import { localDateString } from '@cerebro-diem/core';
import type { Task, TaskStatus } from '@cerebro-diem/core';
import { supabase } from '../../services/supabase';
import TaskItem from '../../components/TaskItem';
import { errorMessage } from '../../services/errors';
import { LIST_LIMIT, queryKeys, reachedLimit } from '../../services/queries';
import { TASK_STATUSES, TASK_STATUS_LABELS } from '../../services/tasks';
import { useContainers } from '../../services/containers';
import { tapTarget } from '../../components/styles';

/** Every task, archived containers included, by status; each shows where it lives and can be moved. */
export default function TasksPage() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<TaskStatus>('pending');
  const today = localDateString();
  const containersQuery = useContainers();

  const tasksQuery = useQuery({
    queryKey: [...queryKeys.taskList, search, activeTab],
    queryFn: async () => {
      let query = supabase
        .from('tasks')
        .select('*')
        .eq('status', activeTab)
        .order('due_date', { ascending: true, nullsFirst: false })
        .limit(LIST_LIMIT);

      if (search) {
        query = query.ilike('name', `%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Task[];
    },
  });
  const tasks = tasksQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <CheckSquare className="w-8 h-8 text-primary" />
        <h1 className="text-2xl font-bold">All tasks</h1>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        {TASK_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setActiveTab(status)}
            className={`${tapTarget} px-4 py-2 rounded-xl font-medium transition-colors ${
              activeTab === status ? 'bg-primary text-on-primary' : 'bg-surface hover:bg-surface-variant'
            }`}
          >
            {TASK_STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-on-surface-variant" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks..."
            aria-label="Search tasks"
            className="w-full min-h-11 pointer-fine:min-h-0 pl-10 pr-4 py-2 text-base rounded-xl border border-outline/30 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {tasksQuery.isError && (
        <p className="text-sm text-error">Could not {tasksQuery.data ? 'refresh' : 'load'} tasks: {errorMessage(tasksQuery.error)}</p>
      )}
      {containersQuery.isError && (
        <p className="text-sm text-error">Could not {containersQuery.data ? 'refresh' : 'load'} your projects and areas: {errorMessage(containersQuery.error)}</p>
      )}
      {tasksQuery.isError && !tasksQuery.data ? null : tasksQuery.isPending ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-surface rounded-xl p-4 h-16" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 text-on-surface-variant">
          <CheckSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No {TASK_STATUS_LABELS[activeTab].toLowerCase()} tasks</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <li key={task.id}>
              <TaskItem task={task} today={today} containers={containersQuery.data ?? []} />
            </li>
          ))}
        </ul>
      )}
      {reachedLimit(tasks) && (
        <p role="status" className="text-sm text-tertiary">
          Showing the first {LIST_LIMIT.toLocaleString()} tasks; search to narrow the list.
        </p>
      )}
    </div>
  );
}
